/**
 * Folder Picker Modal - Select a folder to move a card to
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import { IoCheckmark, IoChevronDown, IoChevronForward, IoFolder, IoFolderOpen } from 'react-icons/io5';

import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import { useFoldersQuery } from '@/features/folders/hooks/api';
import type { Folder } from '@/types/card';

export interface FolderPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentFolderId: string | null;
  onSelect: (folderId: string | null) => void;
  isPending?: boolean;
}

interface FolderNode extends Folder {
  children: FolderNode[];
}

/** Build a tree structure from flat folders list */
function buildFolderTree(folders: Folder[]): FolderNode[] {
  const nodeMap = new Map<string, FolderNode>();
  const roots: FolderNode[] = [];

  // Create nodes
  for (const folder of folders) {
    nodeMap.set(folder.id, { ...folder, children: [] });
  }

  // Build tree
  for (const folder of folders) {
    const node = nodeMap.get(folder.id)!;
    if (folder.parent_id && nodeMap.has(folder.parent_id)) {
      nodeMap.get(folder.parent_id)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  // Sort children
  const sortNodes = (nodes: FolderNode[]) => {
    nodes.sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
    nodes.forEach((n) => sortNodes(n.children));
  };
  sortNodes(roots);

  return roots;
}

interface FolderItemProps {
  folder: FolderNode;
  depth: number;
  selectedId: string | null;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
  onSelect: (id: string) => void;
}

function FolderItem({ folder, depth, selectedId, expandedIds, onToggleExpand, onSelect }: FolderItemProps): ReactElement {
  const isSelected = selectedId === folder.id;
  const hasChildren = folder.children.length > 0;
  const isExpanded = expandedIds.has(folder.id);

  return (
    <div>
      <button
        type="button"
        onClick={() => onSelect(folder.id)}
        className={clsx(
          'group flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors',
          isSelected ? 'bg-aether/20 text-pearl' : 'text-silver hover:bg-slate hover:text-pearl',
        )}
        style={{ paddingLeft: `${12 + depth * 20}px` }}
      >
        {/* Expand/collapse toggle */}
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(folder.id);
            }}
            className="text-ash hover:text-pearl -ml-1 p-0.5"
          >
            {isExpanded ? <IoChevronDown className="h-3.5 w-3.5" /> : <IoChevronForward className="h-3.5 w-3.5" />}
          </button>
        ) : (
          <span className="w-4" />
        )}

        {/* Folder icon */}
        {isExpanded ? (
          <IoFolderOpen className="h-4 w-4 shrink-0" style={{ color: folder.color ?? undefined }} />
        ) : (
          <IoFolder className="h-4 w-4 shrink-0" style={{ color: folder.color ?? undefined }} />
        )}

        {/* Folder name */}
        <span className="flex-1 truncate">{folder.name}</span>

        {/* Selected indicator */}
        {isSelected && <IoCheckmark className="text-aether h-4 w-4 shrink-0" />}
      </button>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div>
          {folder.children.map((child) => (
            <FolderItem
              key={child.id}
              folder={child}
              depth={depth + 1}
              selectedId={selectedId}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function FolderPickerModal({ isOpen, onClose, currentFolderId, onSelect, isPending }: FolderPickerModalProps): ReactElement {
  const { data: folders } = useFoldersQuery();
  const [selectedId, setSelectedId] = useState<string | null>(currentFolderId);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());

  // Reset selection when modal opens
  const prevOpen = useMemo(() => isOpen, [isOpen]);
  if (isOpen && !prevOpen) {
    // This is a workaround since useEffect runs after render
    // We set it directly on the first render when opening
  }

  // Build folder tree
  const folderTree = useMemo(() => (folders ? buildFolderTree(folders) : []), [folders]);

  // Expand parent folders of current selection on open
  useMemo(() => {
    if (isOpen && folders && currentFolderId) {
      const parentsToExpand = new Set<string>();
      let current = folders.find((f) => f.id === currentFolderId);
      while (current?.parent_id) {
        parentsToExpand.add(current.parent_id);
        current = folders.find((f) => f.id === current!.parent_id);
      }
      if (parentsToExpand.size > 0) {
        setExpandedIds((prev) => new Set([...prev, ...parentsToExpand]));
      }
      setSelectedId(currentFolderId);
    }
  }, [isOpen, folders, currentFolderId]);

  const handleToggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    onSelect(selectedId);
  };

  const hasChanges = selectedId !== currentFolderId;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Move to Folder" size="sm">
      <div className="space-y-4">
        {/* Unfiled option */}
        <button
          type="button"
          onClick={() => setSelectedId(null)}
          className={clsx(
            'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors',
            selectedId === null ? 'bg-aether/20 text-pearl' : 'text-silver hover:bg-slate hover:text-pearl',
          )}
        >
          <span className="w-4" />
          <IoFolder className="text-ash h-4 w-4 shrink-0" />
          <span className="flex-1">Unfiled</span>
          {selectedId === null && <IoCheckmark className="text-aether h-4 w-4 shrink-0" />}
        </button>

        {/* Folders list */}
        {folderTree.length > 0 && (
          <div className="border-storm max-h-64 overflow-y-auto rounded-lg border">
            <div className="p-1">
              {folderTree.map((folder) => (
                <FolderItem
                  key={folder.id}
                  folder={folder}
                  depth={0}
                  selectedId={selectedId}
                  expandedIds={expandedIds}
                  onToggleExpand={handleToggleExpand}
                  onSelect={setSelectedId}
                />
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {folderTree.length === 0 && (
          <div className="text-ash py-8 text-center text-sm">
            No folders yet. Create folders from the sidebar to organize your collection.
          </div>
        )}

        {/* Actions */}
        <div className="border-storm flex justify-end gap-3 border-t pt-4">
          <Button type="button" text="Cancel" variant="text" onClick={onClose} disabled={isPending} />
          <Button
            type="button"
            text={isPending ? 'Moving...' : 'Move'}
            color="primary"
            onClick={handleConfirm}
            disabled={!hasChanges || isPending}
          />
        </div>
      </div>
    </Modal>
  );
}
